import type { CashDenomination, CashNotes } from '@six-pockets/shared';
import { parseWhole } from '@/features/manage/draft';

/** The notes a cash count is made of, largest first (the server has the same list). */
export const CASH_DENOMINATIONS: CashDenomination[] = [500, 200, 100, 50, 20, 10];

/** What staff typed for each note, and the coins in ₹: raw text so a field can be empty. */
export type NotesDraft = Record<`${CashDenomination}` | 'coins', string>;

export const EMPTY_NOTES: NotesDraft = { 500: '', 200: '', 100: '', 50: '', 20: '', 10: '', coins: '' };

const FIELDS = [...CASH_DENOMINATIONS, 'coins'] as const;

/** A saved count's notes as the fields show them (0 as empty). */
export function notesDraftOf(notes: CashNotes | null | undefined): NotesDraft {
  if (!notes) return EMPTY_NOTES;
  const draft = { ...EMPTY_NOTES };
  for (const f of FIELDS) draft[f] = notes[f] ? String(notes[f]) : '';
  return draft;
}

/** Whether any note's field, or the coins, has something typed in. */
export const notesFilled = (draft: NotesDraft) => FIELDS.some((f) => draft[f].trim() !== '');

/** The notes and coins, or null when a field isn't a whole number. Empty fields count as 0. */
export function parseNotes(draft: NotesDraft): CashNotes | null {
  const notes: CashNotes = { 500: 0, 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, coins: 0 };
  for (const f of FIELDS) {
    if (draft[f].trim() === '') continue;
    const n = parseWhole(draft[f]);
    if (n === null) return null;
    notes[f] = n;
  }
  return notes;
}

/** What the notes and coins add up to, in ₹. */
export const notesTotal = (notes: CashNotes) => CASH_DENOMINATIONS.reduce((sum, d) => sum + d * notes[d], notes.coins);

/** "₹500 × 3 · ₹200 × 2 · coins ₹17", leaving out what wasn't counted. */
export const describeNotes = (notes: CashNotes) =>
  [...CASH_DENOMINATIONS.filter((d) => notes[d] > 0).map((d) => `₹${d} × ${notes[d]}`), ...(notes.coins > 0 ? [`coins ₹${notes.coins}`] : [])].join(
    ' · ',
  );
